/**
 * URL/routing params derived from window.location. Used by App for route branching.
 */
export interface UrlParams {
  mode: 'widget' | 'headless' | null;
  position: 'left' | 'right';
  trigger: 'immediate' | 'scroll' | 'navigate';
  theme: 'light' | 'dark' | null;
  embed: boolean;
  helper: boolean;
  embedOrigin: string | null;
  isHelpPage: boolean;
  isDemoPage: boolean;
  demoVariant: number | null;
  isApiDocsPage: boolean;
  isAdminPage: boolean;
  isCustomerPage: boolean;
  customerIdFromUrl: string | null;
  isConfigPage: boolean;
  isCallLogsPage: boolean;
  isOperatePage: boolean;
  isPrivacyPage: boolean;
  isTermsPage: boolean;
  isTaskBoardPage: boolean;
  isWorkflowListPage: boolean;
  isCustomerWorkflowEditorPage: boolean;
  isConversationGraphPage: boolean;
  isBotWorkflowEditorPage: boolean;
  conversationBotIDFromUrl: string | null;
  isMoveBotPage: boolean;
  isAcceptTenantPage: boolean;
  isResellerPage: boolean;
  isPackagesPage: boolean;
  isBillingPage: boolean;
  isUsagePage: boolean;
  isAdminBillingPage: boolean;
  isHomePage: boolean;
  isAgentLinkPage: boolean;
}

export function getUrlParams(): UrlParams {
  const params = new URLSearchParams(window.location.search);
  const pathname = window.location.pathname;

  const isCallLogsPage = pathname === '/call-logs' || pathname === '/call-logs/' || pathname.includes('/call-logs');
  const isOperatePage = pathname.includes('/operate');
  const isTaskBoardPage = pathname.match(/^\/customer(\/[^/]+)?\/tasks\/?$/);
  const isWorkflowListPage = pathname.match(/^\/customer(\/[^/]+)?\/workflows\/?$/);
  const isCustomerWorkflowEditorPage = pathname.includes('/workflows/') && (pathname.includes('/edit') || pathname.includes('/new'));
  const isMoveBotPage = pathname === '/move-bot' || pathname === '/move-bot/';
  const isAcceptTenantPage = pathname === '/accept-tenant' || pathname === '/accept-tenant/';
  const isResellerPage = pathname === '/reseller' || pathname === '/reseller/';
  const isPackagesPage = pathname.match(/^\/customer(\/[^/]+)?\/packages\/?$/);
  const isBillingPage = pathname.match(/^\/customer(\/[^/]+)?\/billing\/?$/);
  const isUsagePage = pathname.match(/^\/customer(\/[^/]+)?\/usage\/?$/);
  const isAdminBillingPage = pathname === '/admin/billing' || pathname === '/admin/billing/';
  const customerMatch = pathname.match(/^\/customer\/([a-zA-Z0-9_-]+)\/?$/);
  const customerIdFromUrl = customerMatch ? customerMatch[1] : null;
  // /b/{botID}/conversation — compatibility alias for the current per-bot
  // workflow editor. Keeping the path under /b/ matches the other bot-scoped
  // pages (config, api-docs).
  const convMatch = pathname.match(/^\/b\/([a-zA-Z0-9_-]+)\/conversation\/?$/);
  const isConversationGraphPage = !!convMatch;
  // /b/{botID}/conversation/canvas — full-page React Flow workflow editor
  // for the bot conversation graph.
  const botWorkflowEditorMatch = pathname.match(/^\/b\/([a-zA-Z0-9_-]+)\/conversation\/canvas\/?$/);
  const isBotWorkflowEditorPage = !!botWorkflowEditorMatch;
  const conversationBotIDFromUrl = convMatch ? convMatch[1] : botWorkflowEditorMatch ? botWorkflowEditorMatch[1] : null;
  const isLegacyCustomerPage = pathname === '/customer' || pathname === '/customer/';
  const isHomePage = pathname === '/' || pathname === '';
  // /agent/{token} — public guest agent page (live chat takeover, spec §10).
  const isAgentLinkPage = /^\/agent\/[^/]+\/?$/.test(pathname);

  return {
    mode: params.get('mode') as 'widget' | 'headless' | null,
    position: (params.get('position') || 'right') as 'left' | 'right',
    trigger: (params.get('trigger') || 'immediate') as 'immediate' | 'scroll' | 'navigate',
    theme: params.get('theme') as 'light' | 'dark' | null,
    embed: params.get('embed') === 'true',
    helper: params.get('helper') === 'true',
    embedOrigin: params.get('embed_origin'),
    isHelpPage: pathname.endsWith('/help'),
    isDemoPage: (/\/demo(-[1-5])?$/).test(pathname),
    demoVariant: (() => {
      const m = pathname.match(/\/demo-([1-5])$/);
      if (m) return parseInt(m[1], 10);
      if (pathname.endsWith('/demo')) return 1;
      return null;
    })(),
    isApiDocsPage: pathname.endsWith('/api-docs'),
    // /admin and every v2 section path (/admin/<section>[/<sub>]); the billing
    // console is its own route below. Without the prefix match a hard load of
    // /admin/phone-numbers fell through to the home page.
    isAdminPage: pathname === '/admin' || (pathname.startsWith('/admin/') && !isAdminBillingPage),
    isCustomerPage: isLegacyCustomerPage || !!customerIdFromUrl,
    customerIdFromUrl,
    isConfigPage: pathname.includes('/config'),
    isCallLogsPage,
    isOperatePage,
    isPrivacyPage: pathname === '/privacy' || pathname === '/privacy/',
    isTermsPage: pathname === '/terms' || pathname === '/terms/',
    isTaskBoardPage: !!isTaskBoardPage,
    isWorkflowListPage: !!isWorkflowListPage,
    isCustomerWorkflowEditorPage,
    isConversationGraphPage,
    isBotWorkflowEditorPage,
    conversationBotIDFromUrl,
    isMoveBotPage,
    isAcceptTenantPage,
    isResellerPage,
    isPackagesPage: !!isPackagesPage,
    isBillingPage: !!isBillingPage,
    isUsagePage: !!isUsagePage,
    isAdminBillingPage,
    isHomePage,
    isAgentLinkPage,
  };
}
